import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { memo } from 'react';

import { PageHeaderMeta } from '@/components/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import { getDiscoverPaginationFilter, getShouldDiscoverTopic } from '@/redux/modules/discover';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getItemImportantCategories } from '@/redux/modules/searchFacetAggregates.selectors';
import capitalize from '@/utils/capitalize';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getItemCardImageUrl, logoUrl } from '@/utils/urls';

import { useDiscoverPageCanonicalUrl } from './hooks/useDiscoverPageCanonicalUrl';

type Props = {
    firstItemId: number;
    title: string;
};

const DiscoverPageHeaderMeta = ({ firstItemId, title }: Props) => {
    // useIntl stays for formatNumber only — it has no app-level wrapper the way formatMessage does.
    const { formatNumber } = useIntl();
    const formatMessage = useAppFormatMessage();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, firstItemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, firstItemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, firstItemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, firstItemId));
    const categories = useAppSelector(getItemImportantCategories);
    const { totalRecords } = useAppSelector(getDiscoverPaginationFilter);
    const formattedTotalRecords = formatNumber(totalRecords);
    let capitalizedTitle = capitalize(title);
    const shouldDiscover = useAppSelector((state) => getShouldDiscoverTopic(state, title));
    const firstPhoto = photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, firstItemId, firstPhoto, imageVersion) || logoUrl;
    const canonical = useDiscoverPageCanonicalUrl();
    const [l1First, l1Second] = categories;
    const description =
        categories.length > 1
            ? String(
                  formatMessage(['discoverPage', 'meta', 'descriptionMultipleCategories'], {
                      values: {
                          l1First: l1First?.name || '',
                          l1Second: l1Second?.name || '',
                          term: capitalizedTitle,
                          totalRecords: formattedTotalRecords,
                      },
                  })
              )
            : String(
                  formatMessage(['discoverPage', 'meta', 'description'], {
                      values: {
                          l1First: l1First?.name,
                          term: capitalizedTitle,
                          totalRecords: formattedTotalRecords,
                      },
                  })
              );
    capitalizedTitle = useAddNotificationCountToTitle(capitalizedTitle);

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={capitalizedTitle}
        >
            {!shouldDiscover && (
                <meta
                    content="noindex, nofollow"
                    name="robots"
                />
            )}
        </PageHeaderMeta>
    );
};

export default memo<Props>(DiscoverPageHeaderMeta);
